// Shared helpers for conditional logic and answer piping (used by Survey Builder preview + Public response page)

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  if (Array.isArray(v)) return NaN;
  const n = Number(v);
  return Number.isNaN(n) ? NaN : n;
};

const containsValue = (haystack, needle) => {
  if (haystack === null || haystack === undefined) return false;
  if (Array.isArray(haystack)) return haystack.map(String).includes(String(needle));
  return String(haystack).toLowerCase().includes(String(needle).toLowerCase());
};

export function evalCondition(answer, operator, value) {
  switch (operator) {
    case "equals": {
      if (Array.isArray(answer)) return answer.map(String).includes(String(value));
      return String(answer ?? "") === String(value ?? "");
    }
    case "not_equals":
      return !evalCondition(answer, "equals", value);
    case "contains":
      return containsValue(answer, value);
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const a = toNum(answer);
      const b = toNum(value);
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      if (operator === "gt") return a > b;
      if (operator === "gte") return a >= b;
      if (operator === "lt") return a < b;
      return a <= b;
    }
    case "between": {
      // value can be "min,max" or {min,max} or [min,max]
      const a = toNum(answer);
      if (Number.isNaN(a)) return false;
      let lo, hi;
      if (Array.isArray(value)) { [lo, hi] = value; }
      else if (value && typeof value === "object") { lo = value.min; hi = value.max; }
      else {
        const parts = String(value ?? "").split(/[,\-\s]+/).filter(Boolean);
        [lo, hi] = parts;
      }
      const l = toNum(lo), h = toNum(hi);
      if (Number.isNaN(l) || Number.isNaN(h)) return false;
      const min = Math.min(l, h), max = Math.max(l, h);
      return a >= min && a <= max;
    }
    case "in": {
      // value is comma-separated list, e.g. "0,1,2,3"
      const items = Array.isArray(value)
        ? value
        : String(value ?? "").split(",").map((s) => s.trim()).filter((s) => s !== "");
      if (Array.isArray(answer)) {
        return answer.some((x) => items.map(String).includes(String(x)));
      }
      return items.map(String).includes(String(answer ?? ""));
    }
    case "not_in":
      return !evalCondition(answer, "in", value);
    case "is_empty":
      return answer === undefined || answer === null || answer === "" ||
             (Array.isArray(answer) && answer.length === 0);
    case "is_not_empty":
      return !evalCondition(answer, "is_empty", null);
    default:
      return false;
  }
}

// Returns true if question should be visible given current answers
export function isVisible(q, answers) {
  if (!q.display_when || q.display_when.length === 0) return true;
  const match = q.display_when_match === "any" ? "any" : "all";
  if (match === "any") {
    return q.display_when.some((c) => evalCondition(answers[c.question_id], c.operator, c.value));
  }
  return q.display_when.every((c) => evalCondition(answers[c.question_id], c.operator, c.value));
}

// Resolve next question index given branch_to rules
// Returns: { next: <index> } or { done: true } when jumping past last or to "end"
export function resolveBranch(q, answer, allQuestions, currentIndex, answers) {
  const findNextVisibleFrom = (start) => {
    for (let i = start; i < allQuestions.length; i++) {
      if (isVisible(allQuestions[i], answers)) return i;
    }
    return -1;
  };

  if (q.branch_to && q.branch_to.length > 0) {
    for (const rule of q.branch_to) {
      if (evalCondition(answer, rule.operator, rule.value)) {
        if (rule.jump_to === "end") return { done: true };
        const targetIdx = allQuestions.findIndex((qq) => qq.id === rule.jump_to);
        if (targetIdx >= 0) {
          // If target is hidden, advance to next visible AT OR AFTER target
          if (isVisible(allQuestions[targetIdx], answers)) return { next: targetIdx };
          const nv = findNextVisibleFrom(targetIdx + 1);
          if (nv >= 0) return { next: nv };
          return { done: true };
        }
      }
    }
  }
  // Default: advance to next visible question
  const nv = findNextVisibleFrom(currentIndex + 1);
  if (nv >= 0) return { next: nv };
  return { done: true };
}

// Replace {{q:<id>}} or {{Q<n>}} (1-based) tokens in a string with answer values.
// Falls back to the question title or '___' for missing answers.
export function pipe(text, answers, questions) {
  if (!text) return text;
  let out = String(text);
  // {{q:<id>}} — by question id
  out = out.replace(/\{\{\s*q:([a-zA-Z0-9_-]+)\s*\}\}/g, (_, qid) => {
    const v = answers[qid];
    if (v === undefined || v === null || v === "") {
      const q = (questions || []).find((qq) => qq.id === qid);
      return q ? `[${q.title}]` : "___";
    }
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
  });
  // {{Q<n>}} — by 1-based index, e.g. {{Q3}} or {{q3}}
  out = out.replace(/\{\{\s*[Qq](\d+)\s*\}\}/g, (_, idxStr) => {
    const i = Number(idxStr) - 1;
    const q = (questions || [])[i];
    if (!q) return "___";
    const v = answers[q.id];
    if (v === undefined || v === null || v === "") return `[${q.title}]`;
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
  });
  return out;
}

export const OPERATORS = [
  { id: "equals", label: "equals" },
  { id: "not_equals", label: "does not equal" },
  { id: "contains", label: "contains" },
  { id: "gt", label: ">" },
  { id: "gte", label: "≥" },
  { id: "lt", label: "<" },
  { id: "lte", label: "≤" },
  { id: "between", label: "between" },
  { id: "in", label: "is one of" },
  { id: "not_in", label: "is not one of" },
  { id: "is_empty", label: "is empty" },
  { id: "is_not_empty", label: "is not empty" },
];

export const BRANCH_OPERATORS = OPERATORS.filter((o) => !["is_empty", "is_not_empty"].includes(o.id));
